














































































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import MediaFormModal from '@/web/components/media-library/MediaFormModal.vue';
import LibraryAddMedia from '@/web/views/admin/media-library/LibraryAddMedia.vue';
import MediaItemCard from '@/web/components/media-library/MediaItemCard.vue';
import MediaSearchSection from '@/web/components/media-library/MediaSearchSection.vue';
import draggable from 'vuedraggable';
import { draggableDelay } from '@/web/utils/draggable';
import { debounce } from 'lodash';
import PlaylistsPanel from '@/web/components/playlists/PlaylistsPanel.vue';
import PlaylistTeacherItemCard from '@/web/components/playlists/PlaylistTeacherItemCard.vue';
import { errorToMessage } from '@/core/utils/errors';
import PlaylistContentPanel from '@/web/components/playlists/PlaylistContentPanel.vue';
import PlaylistsMediaFormModal from '@/web/components/media-library/PlaylistsMediaFormModal.vue';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';

interface LocalState {
  draggableDelay: number;
  searchQuery: any;
  contentResponse: PaginatedResponse<ContentResponse>;
  content: ContentResponse[];
  page: number;
  contentExist: boolean;
  mediaItemsSelected: any[];
  mediaItemsSelectedEntity: any[];
  playlists: PlaylistResponse[];
  showPlaylistPanel: boolean;
  loading: boolean;
  selectedPlaylist: any;
}

export default Vue.extend({
  name: 'ProductContent',
  mixins: [TeacherPermissionsMixin],
  components: {
    MediaItemCard,
    MediaSearchSection,
    draggable,
    PlaylistsPanel,
    PlaylistTeacherItemCard,
    PlaylistContentPanel,
  },
  data(): LocalState {
    return {
      draggableDelay,
      searchQuery: null,
      contentResponse: [],
      content: [],
      page: 1,
      contentExist: false,
      mediaItemsSelected: [],
      mediaItemsSelectedEntity: [],
      playlists: [],
      showPlaylistPanel: false,
      selectedPlaylistId: null,
      loading: false,
    };
  },
  async mounted() {
    this.contentExist = await this.getContent();
  },
  computed: {
    ...mapGetters({
      productForm: 'product/productForm',
    }),
    productId(): number {
      return parseInt(this.$route.params['id']);
    },
    query(): any {
      return {
        ...this.searchQuery,
        productId: this.productId,
        page: this.page,
      };
    },
    selectedPlaylist() {
      if (!this.selectedPlaylistId) {
        return {};
      }
      return this.playlists.find(
        playlist => playlist.id === this.selectedPlaylistId
      );
    },
  },
  watch: {
    query: 'getContentDebounced',
  },
  methods: {
    async getContent(): Promise<boolean> {
      this.loading = true;
      this.playlists = await this.$store.dispatch(
        'product/getPlaylists',
        this.productId
      );
      this.contentResponse = await this.$store.dispatch(
        'product/getProductContent',
        this.query
      );
      if (this.contentResponse) {
        this.content = [...this.contentResponse.results];
        this.loading = false;
        return this.content.length > 0;
      }
      this.loading = false;
      return false;
    },
    getContentDebounced: debounce(async function (this) {
      await this.getContent();
    }, 300),
    async reorderContent(): Promise<void> {
      const content = this.content.map((c, index) => {
        return {
          media_id: c.id,
          position: index * -1,
        } as ReorderContentRequest;
      });
      await this.$store.dispatch('product/reorderProductContent', {
        productId: this.productId,
        content: content,
      });
    },
    async openMedia(media) {
      // Open media for editing
      this.$modal.show(
        LibraryAddMedia,
        {
          medaiData: media,
          showAsModal: true,
        },
        { classes: ['vm--inline-modal'] },
        {
          'media-saved': async () => {
            this.getContent();
          },
        }
      );
    },
    async openNewMedia(medaiData) {
      this.$modal.show(
        LibraryAddMedia,
        {
          medaiData,
          showAsModal: true,
        },
        { classes: ['vm--inline-modal'] },
        {
          'media-saved': async media => {
            await this.$store.dispatch('product/createProductContent', {
              productId: this.productId,
              data: {
                content: [media.id],
              },
            });
            await this.getContent();
          },
        }
      );
    },
    async showAddMediaForm(): Promise<void> {
      this.$modal.show(
        MediaFormModal,
        {
          showSelectMedia: true,
          showEditMedia: false,
          videoUploadAllowed: this.videoUploadAllowed,
        },
        { classes: ['vm--inline-modal'], clickToClose: false },
        {
          'media-selected': async (content: any) => {
            await this.$store.dispatch('product/createProductContent', {
              productId: this.productId,
              data: {
                content: content.map(c => c.id),
              },
            });
            this.contentExist = await this.getContent();
          },
          'media-added': medaiData => {
            this.openNewMedia(medaiData);
          },
        }
      );
    },
    async removeContent(id): Promise<void> {
      await this.$store.dispatch('product/deleteProductContent', {
        productId: this.productId,
        contentId: id,
      });
      this.contentExist = await this.getContent();
      this.deselectMedia();
    },
    removeProductContent(ids) {
      ids.forEach(item => {
        this.removeContent(item);
      });
    },
    isMediaItemsSelected(id) {
      return this.mediaItemsSelected.indexOf(id) > -1;
    },
    toggleMediaItem(media) {
      const index = this.mediaItemsSelected.indexOf(media.id);
      if (index === -1) {
        this.mediaItemsSelected.push(media.id);
        this.mediaItemsSelectedEntity.push(media);
      } else {
        this.mediaItemsSelected.splice(index, 1);
        this.mediaItemsSelectedEntity.splice(index, 1);
      }
    },
    deselectMedia() {
      this.mediaItemsSelected = [];
      this.mediaItemsSelectedEntity = [];
    },
    async createPlaylist(playlist) {
      try {
        const playlistResponse = await this.$store.dispatch(
          'product/createPlaylist',
          {
            productId: this.productId,
            playlist,
          }
        );

        this.playlists.push(playlistResponse);

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('PRODUCTS.MODAL.THE_PLAYLIST_WAS_SUCCESSFULLY_CREATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('PRODUCTS.MODAL.FAILED_TO_CREATE_A_PLAYLIST'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async updatePlaylist(playlist) {
      try {
        const playlistResponse = await this.$store.dispatch(
          'product/updatePlaylist',
          {
            productId: this.productId,
            playlist,
          }
        );

        this.playlists.forEach((item, index) => {
          if (item.id === playlistResponse.id) {
            this.playlists[index] = playlistResponse;
            this.playlists = Object.assign([], this.playlists);
          }
        });

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('PRODUCTS.MODAL.THE_PLAYLIST_WAS_SUCCESSFULLY_UPDATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('PRODUCTS.MODAL.FAILED_TO_UPDATE_A_PLAYLIST'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async deletePlaylist(id) {
      try {
        this.hidePlaylistContentPanel();

        await this.$store.dispatch('product/deletePlaylist', {
          productId: this.productId,
          playlistId: id,
        });
        this.playlists = this.playlists.filter(playlist => playlist.id !== id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('PRODUCTS.MODAL.THE_PLAYLIST_WAS_SUCCESSFULLY_DELETED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('PRODUCTS.MODAL.FAILED_TO_DELETE_A_PLAYLIST'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    showPlaylistContentPanel(playlist) {
      this.selectedPlaylistId = playlist.id;
      this.showPlaylistPanel = true;
    },
    hidePlaylistContentPanel() {
      this.selectedPlaylistId = null;
      this.showPlaylistPanel = false;
    },
    async showAddMediaFormPlaylistPanel(): Promise<void> {
      this.$modal.show(
        PlaylistsMediaFormModal,
        {
          showSelectMedia: true,
          showEditMedia: false,
        },
        { classes: ['vm--inline-modal'], clickToClose: false },
        {
          'media-selected': async (content: any) => {
            await this.$store.dispatch('product/createProductContent', {
              productId: this.productId,
              data: {
                content: content.map(c => c.id),
              },
            });
            this.contentExist = await this.getContent();

            content.forEach(item => {
              this.selectedPlaylist.content.push(item);
            });

            this.updatePlaylist(this.selectedPlaylist);
          },
        }
      );
    },
    selectedPlaylistContentRemoved(mediaId) {
      this.selectedPlaylist.content = this.selectedPlaylist.content.filter(
        item => item.id !== mediaId
      );
    },
    reorderPlaylists() {
      try {
        let playlistResponse = {};
        this.playlists.map((playlist, index) => {
          playlist.position = index;
          playlistResponse = this.$store.dispatch('product/updatePlaylist', {
            productId: this.productId,
            playlist,
          });

          if (playlist.id === playlistResponse.id) {
            this.playlists[index] = playlistResponse;
            this.playlists = Object.assign([], this.playlists);
          }
        });

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('PRODUCTS.MODAL.THE_PLAYLIST_WAS_SUCCESSFULLY_UPDATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('PRODUCTS.MODAL.FAILED_TO_UPDATE_A_PLAYLIST'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
  },
});
